import { TbChevronDown, TbDotsVertical, TbRefresh } from "solid-icons/tb";
import {
  batch,
  type Component,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  ErrorBoundary,
  For,
  on,
  onCleanup,
  onMount,
  Show,
  startTransition,
  Suspense,
} from "solid-js";
import { SecondaryCTA } from "@core/components/cta/SecondaryCTA";
import { Loading } from "@core/components/Loading";
import { CollectionKind, type CollectionSnapshot } from "@core/domains/collections/collections.types";
import { useWire } from "@core/wire";
import styles from "./SidebarCollections.module.css";
import { useUIState } from "@core/ui/UIState";
import { CustomEvents } from "@core/ui/custom-events";
import { createStore, produce } from "solid-js/store";
import { getRootCollectionUIOverloads } from "@core/domains/collections/collections.helpers";
import { StIcon } from "./icons";
import { BiSolidDownArrow } from "solid-icons/bi";
import { urls } from "@core/lib/urls";
import { useNavigate } from "@solidjs/router";
import { CollectionsContextMenu } from "./context-menu/CollectionsContextMenu";
import { createElementSize } from "@solid-primitives/resize-observer";
import { throttle } from "@solid-primitives/scheduled";

export const SidebarCollections: Component<{
  /**
   * Callback for when a collection is clicked.
   * The default behavior is to navigate to the collection url.
   * Passing a callback will override the default behavior.
   */
  onCollectionClick?: (collectionId: string) => void;
  filter?: string;
  filterRoots?: CollectionKind[];
  activeCollectionId?: string;
}> = (props) => {
  const wire = useWire();
  const collections = wire.services.collections;
  const navigate = useNavigate();
  const [, { refetch: refetchTree }] = collections.globalResourceTree;
  const isGuest = wire.services.auth.isGuest;

  const ui = useUIState();
  const [menuToggle] = ui.collectionsContextMenu;
  const [collectionId] = ui.collectionId;

  const [collapsed, setCollapsed] = createStore<Record<string, boolean>>({});

  const [ref, setRef] = createSignal<HTMLElement>();
  const size = createElementSize(ref);

  const unhideCollection = (id: string) => {
    const ancestors = wire.services.collections.getAncestorsIds(id);
    batch(() => {
      for (const ancestor of ancestors) {
        setCollapsed(ancestor, false);
      }
    });
  };

  onMount(() => {
    const listener = (e: CustomEvent<{ collectionId: string }>) => {
      const id = e.detail.collectionId;

      if (isHidden(id)) {
        unhideCollection(id);
      }

      if (collapsed[id]) {
        toggleCollapsed(id);
      }

      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-collection-id="${id}"]`);

        const leftBar = ref();
        if (!leftBar || !el) return;

        const barRect = leftBar.getBoundingClientRect();
        let { top } = el.getBoundingClientRect();
        top = Math.round(top);
        const scroll = Math.round(leftBar.scrollTop);

        const newScroll = scroll + top - barRect.top;

        // Check if element is already in view before scrolling
        const leftBarHeight = leftBar.clientHeight;
        const elementHeight = el.clientHeight;
        const elementTop = top - scroll - barRect.top;
        const elementBottom = elementTop + elementHeight;

        // Element is already fully visible, no need to scroll
        if (elementTop >= 0 && elementBottom <= leftBarHeight) {
          return;
        }
        leftBar.scrollTo({ top: newScroll, behavior: "smooth" });
      });
    };
    CustomEvents.scrollCollectionIntoView.add(listener);
    onCleanup(() => {
      CustomEvents.scrollCollectionIntoView.remove(listener);
    });
  });

  const showMenu = (id: string) => {
    const collection = wire.services.collections.getCollection(id);
    if (collection?.collectionKind === CollectionKind.Org && isGuest()) return false;
    return true;
  };

  const memoId = createMemo(() => collectionId());
  createEffect(
    on(memoId, (id) => {
      CustomEvents.scrollCollectionIntoView.dispatch({ collectionId: id });
    }),
  );

  const isHidden = (id: string) => {
    const ancestors = wire.services.collections.getAncestorsIds(id);
    const isCollapsed = ancestors.some((p) => collapsed[p]);

    // Short-circuit if the collection is collapsed
    if (isCollapsed) return true;

    if (!props.filter) {
      return isCollapsed;
    }

    const descendants = wire.services.collections
      .getCollectionDescendantsIds(id)
      .map((c) => wire.services.collections.getCollection(c))
      .filter(Boolean) as CollectionSnapshot[];

    const lowerFilter = props.filter.toLowerCase().replace(/\s/g, "");

    const isFiltered = descendants.some((c) => c.label.toLowerCase().replace(/\s/g, "").includes(lowerFilter));

    return !isFiltered;
  };

  const getHierarchyForRoot = (root: CollectionSnapshot) => {
    const cols = wire.services.collections;

    const dfs: { id: string; depth: number; col: CollectionSnapshot }[] = [{ id: root.id, depth: 0, col: root }];
    const hierarchy: { id: string; depth: number; col: CollectionSnapshot }[] = [];

    while (dfs.length) {
      const current = dfs.pop();
      if (!current) continue;

      const children = cols
        .getChildren(current.id)
        .filter((c) => !isHidden(c.id))
        .sort((a, b) => b.label.localeCompare(a.label, undefined, { sensitivity: "base", ignorePunctuation: true }));

      dfs.push(...children.map((c) => ({ id: c.id, depth: current.depth + 1, col: c })));
      hierarchy.push(current);
    }

    return hierarchy;
  };

  const flatHierarchy = createMemo(() => {
    const cols = wire.services.collections;
    const rootKinds = props.filterRoots || [CollectionKind.Org, CollectionKind.Personal];
    const roots = rootKinds.map((k) => cols.getRootCollectionByKind(k));
    if (roots.some((r) => !r)) return [];
    return roots.flatMap((r) => getHierarchyForRoot(r!));
  });

  let once = false;
  createEffect(() => {
    if (once) return;
    if (flatHierarchy().length === 0) return;

    const storage = localStorage.getItem("sidebar-collapsed:v1");
    if (!storage) {
      once = true;
      batch(() => {
        for (const c of flatHierarchy()) {
          if (c.depth >= 1) {
            setCollapsed(c.id, true);
          }
        }
      });
      return;
    }
    once = true;

    try {
      const parsed = JSON.parse(storage);
      if (parsed) {
        setCollapsed(parsed);
      }
    } catch (error) {
      console.error(error);
    }
  });

  const toggleCollapsed = (id: string) => {
    setCollapsed(
      produce((data) => {
        const val = data[id];
        data[id] = !val;
      }),
    );

    localStorage.setItem("sidebar-collapsed:v1", JSON.stringify(collapsed));
  };

  const onCollectionClick = (id: string) => {
    if (props.onCollectionClick) {
      props.onCollectionClick(id);
    } else {
      navigate(urls.collection(id));
    }
  };

  const [virtualList, setVirtualList] = createSignal({
    start: 0,
    end: 0,
  });

  const updateList = throttle(() => {
    const el = ref();
    if (!el) return;
    const { scrollTop } = el;
    const remInPx = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    const rowHeight = remInPx * 3;
    const start = Math.floor(scrollTop / rowHeight) - 5;
    const end = start + Math.ceil((size.height || 0) / rowHeight) + 5;
    setVirtualList({ start, end });
  }, 15);

  createEffect(on(() => size.height, updateList));
  onMount(() => {
    const el = ref();
    if (!el) return;

    el.addEventListener("scroll", updateList);
    onCleanup(() => {
      el.removeEventListener("scroll", updateList);
    });
  });

  return (
    <Suspense fallback={<Loading />}>
      <ErrorBoundary
        fallback={(_err, reset) => (
          <div class={styles["sidebar-collections__error-boundary"]}>
            <p class={styles["sidebar-collections__error-boundary-text"]}>
              There was an error loading your Collections.
            </p>
            <div>
              <SecondaryCTA
                size="small"
                data-test-id="sidebar-collections-error-boundary"
                accessiblePrefix="Click to "
                icon={TbRefresh}
                label="Reload"
                onClick={() => {
                  reset();
                  startTransition(() => refetchTree());
                }}
              />
            </div>
          </div>
        )}
      >
        <div ref={setRef} class="h-full overflow-y-auto">
          <For each={flatHierarchy()}>
            {(c, index) => (
              <Show
                when={index() >= virtualList().start && index() <= virtualList().end}
                fallback={<div class="h-12" aria-hidden data-collection-id={c.id} />}
              >
                <SidebarCollection
                  id={c.id}
                  depth={c.depth}
                  collection={c.col}
                  activeCollectionId={props.activeCollectionId || collectionId()}
                  menuToggle={menuToggle()}
                  isCollapsed={!!collapsed[c.id]}
                  onCollectionClick={onCollectionClick}
                  toggleCollapsed={toggleCollapsed}
                  showMenu={showMenu}
                  refetchTree={refetchTree}
                />
              </Show>
            )}
          </For>
        </div>
      </ErrorBoundary>
    </Suspense>
  );
};

const SidebarCollection: Component<{
  id: string;
  depth: number;
  collection: CollectionSnapshot;
  activeCollectionId: string;
  isCollapsed: boolean;
  menuToggle: string;
  onCollectionClick: (id: string) => void;
  toggleCollapsed: (id: string) => void;
  showMenu: (id: string) => boolean;
  refetchTree: () => void;
}> = (props) => {
  const wire = useWire();

  const ui = useUIState();
  const [, setLeftDrawer] = ui.leftDrawer;

  const override = () => getRootCollectionUIOverloads(props.collection);
  const hasChildren = wire.services.collections.getChildrenIds(props.id).length > 0;
  const isSelected = () => props.activeCollectionId === props.id;

  const [configRef, setConfigRef] = createSignal<HTMLElement>((<></>) as HTMLElement);
  const uniqueId = createUniqueId();
  const accordionMenuId = () =>
    `accordion-menu-${props.collection.label.replace(/\s/g, "-").toLowerCase()}-${uniqueId}`;

  return (
    <div class="relative h-12">
      <Show when={props.depth > 0}>
        <For each={new Array(props.depth).fill(0)}>
          {(_, i) => (
            <div
              class="absolute top-0 bottom-0 w-px h-full bg-surface-decoration"
              style={{ left: `${i() * 1.4 + 1.2}rem` }}
            />
          )}
        </For>
      </Show>

      <div
        class="group h-12 flex items-center text-on-surface text-lg px-2 py-2 rounded-lg min-w-0 min-h-0 w-full"
        classList={{
          "bg-surface-decoration": isSelected(),
          "hover:bg-surface-hover": !isSelected(),
        }}
        style={{ "padding-left": `${props.depth * 1.4 + 0.5}rem` }}
      >
        <Show
          when={hasChildren || props.depth === 0}
          fallback={
            <button
              type="button"
              class="text-on-surface text-lg h-8 w-8 rounded-lg min-w-0 min-h-0 flex items-center justify-center"
              on:click={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onCollectionClick(props.id);
                setLeftDrawer(false);
              }}
            >
              <StIcon
                icon={override().icon}
                classList={{
                  "text-on-background": props.depth !== 0,
                  "text-on-surface-primary": props.depth === 0,
                }}
              />
            </button>
          }
        >
          <button
            type="button"
            class="text-on-surface text-lg h-8 w-8 rounded-lg min-w-0 min-h-0 flex items-center justify-center"
            on:click={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.toggleCollapsed(props.id);
            }}
          >
            <StIcon
              icon={BiSolidDownArrow}
              class="transition-transform"
              size={8}
              classList={{
                "-rotate-90": props.isCollapsed,
              }}
            />
          </button>
        </Show>

        <button
          data-component="sidebar-accordion-trigger"
          data-collection-id={props.id}
          type="button"
          class="flex items-center pl-2 gap-2 min-w-0 min-h-0 overflow-hidden flex-auto"
          on:click={(e) => {
            e.preventDefault();
            e.stopPropagation();
            props.onCollectionClick(props.id);
            setLeftDrawer(false);
          }}
          title={props.collection.label}
        >
          <Show when={props.depth === 0}>
            <StIcon icon={override().icon} class="size-5 text-on-surface-primary" />
          </Show>
          <span class="truncate">{props.collection.label}</span>
        </button>

        <Show when={props.showMenu(props.id)}>
          <button
            type="button"
            aria-controls={accordionMenuId()}
            aria-expanded={props.menuToggle === accordionMenuId()}
            aria-haspopup="true"
            tabIndex={0}
            ref={(ref) => setConfigRef(ref)}
            class="min-w-0 min-h-0 w-8 flex items-center justify-center opacity-0 group-hover:opacity-100 text-on-surface-primary hover:text-on-surface"
          >
            <span class="sr-only">{`Open ${props.collection.label} action menu.`}</span>
            <StIcon icon={TbDotsVertical} class="size-6" />
          </button>
          <CollectionsContextMenu
            collectionId={props.id}
            bound={configRef()}
            id={accordionMenuId()}
            placement="bottom-start"
            onCollectionCreated={() => props.refetchTree()}
          />
        </Show>
      </div>
    </div>
  );
};
